import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, RefreshCw, DollarSign, CreditCard, FlaskConical, ChevronLeft, ChevronRight, CheckCircle, XCircle, TrendingUp, Users, PieChart as PieIcon, BarChart3, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { API_URL } from '../../config/api';
import jspdf from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ROWS_PER_PAGE = 10;

const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getPresetRange = (preset) => {
    const now = new Date();
    const today = toYMD(now);
    switch (preset) {
        case 'today': return { start: today, end: today };
        case 'week': { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return { start: toYMD(s), end: today }; }
        case 'month': return { start: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
        case 'quarter': { const q = Math.floor(now.getMonth() / 3) * 3; return { start: toYMD(new Date(now.getFullYear(), q, 1)), end: today }; }
        case 'year': return { start: toYMD(new Date(now.getFullYear(), 0, 1)), end: today };
        default: return { start: today, end: today };
    }
};

const fmt = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const PAYMENT_METHOD_LABELS = {
    efectivo: 'Efectivo',
    tarjeta_debito: 'Tarjeta Débito',
    tarjeta_credito: 'Tarjeta Crédito',
    transferencia: 'Transferencia',
    cheque: 'Cheque',
    tarjeta: 'Tarjeta'
};

const getPaymentLabel = (m) => PAYMENT_METHOD_LABELS[m] || m || '-';

const SkeletonStudies = () => (
    <>
        <div className="skeleton-grid">
            {[1, 2].map(i => <div className="skeleton-card" key={i}><div className="skeleton-icon" /><div className="skeleton-text"><div className="skeleton-line short" /><div className="skeleton-line tall" /></div></div>)}
        </div>
        <div className="charts-row"><div className="skeleton-chart" style={{ height: '300px' }}><div className="skeleton-inner" /></div></div>
    </>
);

const BillingStudiesReport = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [activePreset, setActivePreset] = useState('month');
    const [dateRange, setDateRange] = useState(() => getPresetRange('month'));
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ data: [], summary: { total_studies: 0, by_study: {}, by_doctor: {} } });
    const [exportOpen, setExportOpen] = useState(false);

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/reports/billing-studies?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) throw new Error('Error al cargar reporte de estudios');
            const result = await res.json();
            setData(result);
            setPage(1);
        } catch (err) { setError(err.message); } finally { setLoading(false); setRefreshing(false); }
    }, [dateRange]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleExportPDF = () => {
        const doc = new jspdf('l', 'pt', 'a4');
        doc.setFontSize(18);
        doc.text("Reporte de Cobranza - Estudios", 40, 40);
        doc.setFontSize(11);
        doc.text(`Periodo: ${dateRange.start} a ${dateRange.end}`, 40, 60);

        const tableData = data.data.map(item => [
            item.paciente || '-',
            item.estudio || '-',
            fmt(item.costo_estudio),
            getPaymentLabel(item.metodo_pago),
            item.medico_solicitante || 'N/A',
            item.laboratorio || 'Interno',
            item.solicita_factura == 1 ? 'Sí' : 'No',
            item.factura_entregada == 1 ? 'Sí' : 'No',
            item.fecha_toma ? new Date(item.fecha_toma).toLocaleDateString() : 'N/D'
        ]);

        autoTable(doc, {
            startY: 80,
            head: [['Paciente', 'Estudio', 'Costo', 'Método', 'Médico', 'Laboratorio', 'F. Solicitada', 'F. Entregada', 'Fecha']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [8, 54, 119] }
        });

        doc.save(`Reporte_Estudios_${dateRange.start}_${dateRange.end}.pdf`);
        setExportOpen(false);
    };

    const handleExportExcel = () => {
        const exportData = data.data.map(item => ({
            'Paciente': item.paciente || '-',
            'Estudio': item.estudio || '-',
            'Costo': item.costo_estudio || 0,
            'Método de Pago': getPaymentLabel(item.metodo_pago),
            'Médico Solicitante': item.medico_solicitante || 'N/A',
            'Laboratorio Subrogado': item.laboratorio || 'Interno',
            'Factura Solicitada': item.solicita_factura == 1 ? 'Sí' : 'No',
            'Factura Entregada': item.factura_entregada == 1 ? 'Sí' : 'No',
            'Fecha Toma': item.fecha_toma || 'N/D'
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Estudios");
        XLSX.writeFile(wb, `Reporte_Estudios_${dateRange.start}_${dateRange.end}.xlsx`);
        setExportOpen(false);
    };

    const handlePreset = (p) => { setActivePreset(p); setDateRange(getPresetRange(p)); };
    const handleDateChange = (e) => { setActivePreset(''); setDateRange(prev => ({ ...prev, [e.target.name]: e.target.value })); };

    const totalPages = Math.max(1, Math.ceil(data.data.length / ROWS_PER_PAGE));
    const paginatedItems = useMemo(() => {
        const start = (page - 1) * ROWS_PER_PAGE;
        return data.data.slice(start, start + ROWS_PER_PAGE);
    }, [data.data, page]);

    const studyChartData = useMemo(() => {
        return Object.entries(data.summary.by_study)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);
    }, [data.summary.by_study]);

    const doctorChartData = useMemo(() => {
        return Object.entries(data.summary.by_doctor)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [data.summary.by_doctor]);

    const COLORS = ['#083677', '#00BBBF', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

    return (
        <div className="report-view reports-hub-content custom-scrollbar">
            {/* Header */}
            <div className="report-header-actions">
                <div className="report-title-wrapper">
                    <div className="report-title">
                        <BarChart3 size={28} />
                        <h2>Cobranza de Estudios</h2>
                    </div>
                    <p>Detalle de facturación de estudios por tipo y médico</p>
                </div>
                <div className="report-controls">
                    <div className="date-range-picker">
                        <Calendar size={16} />
                        <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} className="date-input" />
                        <span>a</span>
                        <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} className="date-input" />
                    </div>
                    <button className={`btn-icon ${refreshing ? 'spinning' : ''}`} onClick={() => fetchData(true)} disabled={loading || refreshing} title="Actualizar">
                        <RefreshCw size={18} />
                    </button>
                    <div className="export-menu-container" style={{ position: 'relative' }}>
                        <button className="btn-secondary" onClick={() => setExportOpen(!exportOpen)} title="Exportar">
                            <Download size={18} />
                            <span>Exportar</span>
                        </button>
                        {exportOpen && (
                            <div className="export-dropdown" style={{ 
                                position: 'absolute', top: '100%', right: 0, 
                                background: 'white', border: '1px solid #e2e8f0', 
                                borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 1000, minWidth: '160px', marginTop: '8px',
                                animation: 'dropdown-in 0.2s ease-out', overflow: 'hidden'
                            }}>
                                <button onClick={handleExportPDF} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileText size={16} color="#ef4444" />
                                    Exportar PDF
                                </button>
                                <button onClick={handleExportExcel} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    borderTop: '1px solid #f1f5f9', fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileSpreadsheet size={16} color="#10b981" />
                                    Exportar Excel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Presets */}
            <div className="date-presets" style={{ marginBottom: '1.5rem' }}>
                {[
                    { key: 'today', label: 'Hoy' },
                    { key: 'week', label: 'Semana' },
                    { key: 'month', label: 'Mes' },
                    { key: 'quarter', label: 'Trimestre' },
                    { key: 'year', label: 'Año' }
                ].map(p => (
                    <button
                        key={p.key}
                        className={`preset-chip ${activePreset === p.key ? 'active' : ''}`}
                        onClick={() => handlePreset(p.key)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {error ? (
                <div className="error-banner">{error}</div>
            ) : loading ? (
                <SkeletonStudies />
            ) : (
                <>
                    <div className="report-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                        <div className="kpi-card highlight">
                            <div className="kpi-icon-wrapper studies">
                                <FlaskConical size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Total de Estudios</span>
                                <span className="kpi-value">{data.summary.total_studies}</span>
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper income">
                                <TrendingUp size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Ingreso Estimado</span>
                                <span className="kpi-value">
                                    {fmt(data.data.reduce((sum, item) => sum + parseFloat(item.costo_estudio || 0), 0))}
                                </span>
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper ticket">
                                <DollarSign size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Ticket Promedio</span>
                                <span className="kpi-value">
                                    {fmt(data.summary.total_studies > 0 
                                        ? data.data.reduce((sum, item) => sum + parseFloat(item.costo_estudio || 0), 0) / data.summary.total_studies 
                                        : 0)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="charts-row" style={{ gridTemplateColumns: '1.2fr 1fr', gap: '1.5rem' }}>
                        <div className="chart-container">
                            <h3 className="chart-title"><PieIcon size={18} /> Top Estudios</h3>
                            <div style={{ width: '100%', height: 300, display: 'flex', alignItems: 'center' }}>
                                <div style={{ flex: 1, height: '100%' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={studyChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {studyChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div style={{ flex: 1, paddingLeft: '1rem' }}>
                                    {studyChartData.slice(0, 5).map((entry, index) => (
                                        <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: COLORS[index % COLORS.length] }} />
                                            <span style={{ color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.name}</span>
                                            <span style={{ fontWeight: 'bold', color: '#1e293b', marginLeft: 'auto' }}>{entry.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="chart-container">
                            <h3 className="chart-title"><Users size={18} /> Resumen por Médico</h3>
                            <div style={{ width: '100%', height: 300, marginTop: '1rem' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={doctorChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                        <XAxis type="number" hide />
                                        <YAxis 
                                            dataKey="name" 
                                            type="category" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fill: '#64748b', fontSize: 11 }}
                                            width={100}
                                        />
                                        <Tooltip 
                                            cursor={{ fill: 'rgba(8, 54, 119, 0.05)' }}
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                        />
                                        <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                                            {doctorChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    <div className="table-card" style={{ marginTop: '1.5rem' }}>
                        <div className="table-header-flex">
                            <h3><CreditCard size={18} /> Detalle de Estudios</h3>
                            <span className="results-count">{data.data.length} resultados</span>
                        </div>
                        <div className="table-responsive">
                            <table className="data-table" style={{ minWidth: '900px' }}>
                                <thead>
                                    <tr>
                                        <th>Paciente / Fecha Toma</th>
                                        <th>Estudio / Laboratorio</th>
                                        <th>Médico</th>
                                        <th>Fechas</th>
                                        <th>Factura</th>
                                        <th>Método</th>
                                        <th style={{ textAlign: 'right' }}>Costo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedItems.length > 0 ? paginatedItems.map((s, i) => (
                                        <tr key={i}>
                                            <td>
                                                <strong>{s.paciente}</strong><br />
                                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Muestra: {s.fecha_toma ? new Date(s.fecha_toma).toLocaleDateString() : 'N/D'}</span>
                                            </td>
                                            <td>
                                                <strong>{s.estudio}</strong><br />
                                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Lab: {s.laboratorio || 'No Subrogado'}</span>
                                            </td>
                                            <td>{s.medico_solicitante}</td>
                                            <td>
                                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Entrega: {s.fecha_entrega ? new Date(s.fecha_entrega).toLocaleDateString() : 'N/D'}</span>
                                            </td>
                                            <td>
                                                {s.solicita_factura == 1 ? (
                                                    <span style={{
                                                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '500',
                                                        backgroundColor: s.factura_entregada == 1 ? '#dcfce7' : '#fef9c3',
                                                        color: s.factura_entregada == 1 ? '#166534' : '#854d0e',
                                                        border: `1px solid ${s.factura_entregada == 1 ? '#bbf7d0' : '#fef08a'}`
                                                    }}>
                                                        {s.factura_entregada == 1 ? 'Facturado' : 'Pendiente'}
                                                    </span>
                                                ) : <span className="text-muted">No solicitada</span>}
                                            </td>
                                            <td>
                                                <span className="payment-badge">{getPaymentLabel(s.metodo_pago)}</span>
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmt(s.costo_estudio)}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="7" className="text-center text-muted" style={{ padding: '2rem' }}>No hay datos para este periodo</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft size={16} /></button>
                                <span>Página {page} de {totalPages}</span>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight size={16} /></button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default BillingStudiesReport;
